import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { motion } from "framer-motion";
import { useLang } from "@/lib/lang";

import PageHero from "@/components/PageHero";
import SEO from "@/components/SEO";
import ImageLightbox from "@/components/ImageLightbox";
const GRID_SPANS = [
  "col-span-12 md:col-span-8 row-span-2",
  "col-span-6 md:col-span-4",
  "col-span-6 md:col-span-4",
  "col-span-6 md:col-span-4",
  "col-span-12 md:col-span-8",
];



// Fallback demo images if gallery is empty (curated)
const DEMO = [
  { category: "Wedding", title: "Wedding Ritual", image_data: "/site-images/drive/photo-01.jpg" },
  { category: "Wedding", title: "Together", image_data: "/site-images/drive/photo-02.jpg" },
  { category: "Portrait", title: "Bridal Quiet", image_data: "/site-images/drive/photo-03.jpg" },
  { category: "Event", title: "Haldi Glow", image_data: "/site-images/drive/photo-04.jpg" },
  { category: "Pre-wedding", title: "Golden Hour", image_data: "/site-images/drive/photo-05.jpg" },
  { category: "Pre-wedding", title: "Just Us", image_data: "/site-images/drive/photo-06.jpg" },
];

export default function Portfolio() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(12);
  const [categories, setCategories] = useState(["Wedding", "Pre-wedding", "Portrait", "Event", "Commercial"]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/master/portfolio-categories").then((r) => setCategories(r.data.map((x) => x.name))).catch(() => {});
    api
      .get("/gallery")
      .then((r) => setItems(r.data.length ? r.data : DEMO))
      .catch(() => setItems(DEMO))
      .finally(() => setLoading(false));
  }, []);

  const filtered =
    filter === "All" ? items : items.filter((i) => i.category === filter);
  const visible = filtered.slice(0, visibleCount);

  return (
    <><SEO title="Wedding Photography Portfolio in Egra | Wedding Touch" description="Explore Wedding Touch's wedding photography portfolio from Egra and Purba Medinipur, including candid wedding moments, bridal portraits, rituals and pre-wedding stories." canonical="/portfolio" /><div data-testid="portfolio-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      {/* UNIQUE_PAGE_HERO */}
      <PageHero image="/site-images/drive/hero-portfolio.jpg" mobileImage="/site-images/drive/photo-19.jpg" heroKey="portfolio" eyebrow="THE ARCHIVE" title="Stories we" accent="keep." subtitle="A living archive of weddings, portraits, rituals and honest in-between moments." />
      <div className="mb-16">
        <div className="overline mb-4">{t("portfolio.overline")}</div>
        <h1 className="font-serif-display text-5xl md:text-7xl">{t("portfolio.title")}</h1>
      </div>

      <div className="flex flex-wrap gap-6 md:gap-10 mb-14">
        {["All", ...categories].map((c) => (
          <button
            key={c}
            data-testid={`filter-${c.toLowerCase()}`}
            onClick={() => setFilter(c)}
            className={`overline hover-underline ${
              filter === c ? "text-white" : "text-neutral-500"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-neutral-500">Loading…</p>
      ) : filtered.length === 0 ? (
        <p className="text-neutral-500">No images in this category yet.</p>
      ) : (
        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {visible.map((img, i) => (
            <motion.div
              key={img.id || i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.9, delay: (i % 6) * 0.06 }}
              className={GRID_SPANS[i % 5]}
            >
              <button type="button" className="group block w-full overflow-hidden aspect-[4/5] cursor-zoom-in" onClick={() => setLightbox(i)} aria-label={`Open ${img.title || "portfolio image"}`}>
                <img
                  src={img.image_data}
                  alt={img.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
                />
              </button>
              <div className="mt-3 flex justify-between text-sm">
                <span className="text-neutral-300">{img.title}</span>
                <span className="overline">{img.category}</span>
              </div>
            </motion.div>
          ))}
        </div>
      )}
      <ImageLightbox items={visible} index={lightbox} onClose={()=>setLightbox(null)} onChange={setLightbox}/>
      {!loading && visibleCount < filtered.length && <div className="text-center mt-10"><button onClick={()=>setVisibleCount(v=>v+12)} className="border border-neutral-600 px-8 py-3 overline">Load more</button></div>}
    </div></>
  );
}